import React, { useState, useMemo } from 'react';
import { Plus, Save } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import BaseDataTable, {
  ColumnType,
  IColumnConfig,
  IDataRow,
} from '../../../../components/ui/baseDataTable';
import {cn } from '../../../../lib/utils';
import { showSuccessToast, showErrorToast } from '../../../../lib/toast';
import ButtonV2 from '../../../../components/ui/button/v2';
import { PolicyImprovement as PolicyImprovementAPI } from '../../../../api/Recommendations';
import { selectCurrentRecommendation } from '../../logic/Slice';
import {
  updateRecommendationsThunk,
} from '../../logic/Thunk';
import ClosureNoteModal from '../ActionAgainstInvolvedPartiesTable/ClosureNoteModal';
import EditableExpandableTextCell from './EditableExpandableTextCell';
import NoDataIcon from '../../../../assets/svg/EmptyTableIcon';

interface PolicyImprovement {
  id: string;
  suggestedPolicyChange: string;
  approvedBy?: string | null;
  implementationDate?: Date | null;
  closureNote?: string | null;
}

interface PolicyImprovementsTableProps {
  caseId: string;
}

const mapPolicyImprovementToTableData = (
  policy: PolicyImprovementAPI,
  index: number
): PolicyImprovement => ({
  id: `${index}-policy.policy_change.slice(0, 20)`,
  suggestedPolicyChange: policy.policy_change,
  approvedBy: policy.approver || '',
  implementationDate: policy.date,
  closureNote: policy.comments || ''
});

const PolicyImprovementsTable: React.FC<PolicyImprovementsTableProps> = ({
  caseId,
}) => {
  const dispatch = useAppDispatch();
  const recommendations = useAppSelector((state) =>
    selectCurrentRecommendation(state, caseId)
  );

  const [policyEdited, setPolicyEdited] = useState<
    Map<string, PolicyImprovement>
  >(new Map());
  const [policyAdded, setPolicyAdded] = useState<PolicyImprovement[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [isClosureNoteModalOpen, setIsClosureNoteModalOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [modalVariant, setModalVariant] = useState<'edit' | 'view'>('edit');

  const baseData: PolicyImprovement[] = useMemo(() => {
    return (
      recommendations?.policy_improvements?.map((item,index)=>mapPolicyImprovementToTableData(item, index)) || []
    );
  }, [recommendations]);

  const tableData: PolicyImprovement[] = useMemo(() => {
    const rowMap = new Map<string, PolicyImprovement>();

    baseData.forEach((item) => {
      rowMap.set(item.id, item);
    });

    policyEdited.forEach((editedRow, id) => {
      rowMap.set(id, editedRow);
    });

    const orderedRows = Array.from(rowMap.values());
    return [...orderedRows, ...policyAdded];
  }, [baseData, policyEdited, policyAdded]);

  const hasEditedChanges = policyEdited.size > 0;
  const hasAddedRows = policyAdded.length > 0;
  const hasAnyChanges = hasEditedChanges || hasAddedRows;


  const isPolicyRowComplete = (row: PolicyImprovement) => {
    return (
      row.suggestedPolicyChange.trim().length > 0
    );
  };

  const hasInvalidAddedRow = policyAdded.some(
    (row) => !isPolicyRowComplete(row)
  );

  const generateId = () =>
    `policy-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;


  const handleClosureNoteClick = (rowIndex: number) => {
  const row = tableData[rowIndex];
  const hasNote = row?.closureNote && row.closureNote.trim().length > 0;

  setSelectedRowIndex(rowIndex);
  setModalVariant(hasNote ? 'view' : 'edit');
  setIsClosureNoteModalOpen(true);
};

 const handleSaveClosureNote = (note: string) => {
  if (selectedRowIndex === null) return;

  handleTableChange(
    selectedRowIndex,
    note,
    'closureNote'
  );
 };

 const handleCloseClosureNoteModal = () => {
  setIsClosureNoteModalOpen(false);
  setSelectedRowIndex(null);
 };

 const handleAddRow = () => {
    if (policyAdded.length > 0) {
      return;
    }

    const newRow: PolicyImprovement = {
      id: generateId(),
      suggestedPolicyChange: '',
      approvedBy: '',
      implementationDate: null,
      closureNote:'',
    };
    setPolicyAdded((prev) => [...prev, newRow]);
  };

  const handleDeleteRow = async (rowIndex: number) => {
  const rowToDelete = tableData[rowIndex];
  if (!rowToDelete) return;

  const addedIndex = policyAdded.findIndex(p => p.id === rowToDelete.id);
  if (addedIndex !== -1) {
    setPolicyAdded(prev => prev.filter((_, i) => i !== addedIndex));
    return;
  }

  try {
    setIsSaving(true);

    const remainingPolicies = baseData
      .filter(row => row.id !== rowToDelete.id)
      .map(row => ({
        policy_change: row.suggestedPolicyChange,
        approver: row.approvedBy?.trim() || null,
        date: row.implementationDate || null,
        comments: row.closureNote?.trim() || null,
        ai_generated: false,
        user_updated: true,
      }));

    await dispatch(
      updateRecommendationsThunk({
        caseId,
        request: { policy_improvements: remainingPolicies },
      })
    ).unwrap();

    setPolicyEdited(prev => {
      const next = new Map(prev);
      next.delete(rowToDelete.id); 
      return next;
    });

    showSuccessToast('Policy improvement deleted successfully');
  } catch (error) {
    showErrorToast('Failed to delete policy improvement');
    console.error(error);
  } finally {
    setIsSaving(false);
  }
  };


  const handleTableChange = <T extends keyof PolicyImprovement>(
    rowIndex: number,
    value: PolicyImprovement[T],
    field: T
  ) => {
    if(isSaving) return;
    const currentRow = tableData[rowIndex];
    if (!currentRow) return;

    const updatedRow = { ...currentRow, [field]: value };
    const addedIndex = policyAdded.findIndex((p) => p.id === currentRow.id);

    if (addedIndex !== -1) {
      setPolicyAdded((prev) => {
        const newAdded = [...prev];
        newAdded[addedIndex] = updatedRow;
        return newAdded;
      });
    } else {
      setPolicyEdited((prev) => {
        const newEdited = new Map(prev);
        newEdited.set(currentRow.id, updatedRow);
        return newEdited;
      });
    }
  };

  const handleDiscardChanges = () => {
    setPolicyEdited(new Map());
    setPolicyAdded([]);
  };

  const handleSave = async () => {
    if (!hasAnyChanges) return;

    try {
      setIsSaving(true);

      const existingDataMap = new Map(baseData.map((row) => [row.id, row]));

      policyEdited.forEach((edited, id) => {
        existingDataMap.set(id, edited);
      });

      const mergedData = Array.from(existingDataMap.values());
      const allData = [...mergedData, ...policyAdded];

      const policy_improvements_payload : PolicyImprovementAPI[] = allData.map((row) => ({
        policy_change: row.suggestedPolicyChange,
        approver: row.approvedBy?.trim() || null,
        date: row.implementationDate || null,
        comments: row.closureNote?.trim() || null,
        ai_generated: false,
        user_updated: true,
      })) as PolicyImprovementAPI[];

      await dispatch(
        updateRecommendationsThunk({
          caseId,
          request: { policy_improvements: policy_improvements_payload },
        })
      ).unwrap();

      showSuccessToast('Policy improvements saved successfully!');
      setPolicyEdited(new Map());
      setPolicyAdded([]);
    } catch (error) {
      showErrorToast('Failed to save policy improvements');
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const columnConfigs: IColumnConfig<PolicyImprovement>[] = [
  {
    headerName: "Suggested Policy Change",
    field: "suggestedPolicyChange",
    type: "custom",
    getValue: (data) => data.suggestedPolicyChange,
    render: (rowIndex, value) => (
      <EditableExpandableTextCell
        value={value as string}
        onChange={(val) =>
          handleTableChange(rowIndex, val, "suggestedPolicyChange")
        }
      />
    ),
  },
  {
    headerName: 'Approved By',
    field: 'approvedBy',
    type: 'text' as ColumnType,
    getValue: (data: PolicyImprovement) => data.approvedBy,
    isEditable: true,
    onChange: handleTableChange,
    textCellProps: {
      numberOfRows: 1,
      className: "px-0 py-0 items-start min-w-[150px]",
      containerClassName: "px-0 py-0 min-w-[150px]",
      textareaClassName: "px-0",
    },
  },
  {
    headerName: 'Implementation Date',
    field: 'implementationDate',
    type: 'date' as ColumnType,
    getValue: (data: PolicyImprovement) => data.implementationDate,
    isEditable: true,
    onChange: handleTableChange,
    dateCellProps: {
      className: "px-0 py-0",
      inputClassName: "px-0 py-0 bg-transparent min-w-[120px]",
    },
  },
  {
    headerName: "Closure Note",
    field: "closureNote",
    type: "textbutton" as ColumnType,
    getValue: (data: PolicyImprovement) => {
      const hasNote =
        data?.closureNote && data.closureNote.trim().length > 0;
      return hasNote ? "View notes" : "+ Add closure note";
    },
    onClick: handleClosureNoteClick,
    textButtonCellProps: {
      className: "px-0 min-w-[120px]",
      buttonClassName: "px-0",
    },
  },
  ];

  const dataRows: IDataRow<PolicyImprovement>[] = tableData.map((item) => ({
    data: item,
    isEditable: true,
    widgets: isSaving ? [] : (['delete', 'add'] as ('delete' | 'add')[]),
    onDelete: handleDeleteRow,
    onAdd: handleAddRow,
  }));

  const renderDefaultTableBody = () => (
    <div className="flex flex-col items-center justify-center py-8 px-4">
      <NoDataIcon />
      <span className="text-[#0A0A0A] font-sans font-semibold text-base leading-[1.5rem] text-center mt-4 mb-2 max-w-full">
        No policy improvements yet
      </span>
      <span className="text-[#737373] font-sans font-normal text-sm leading-[1.25rem] text-center mb-4 max-w-md">
        Suggest improvements to strengthen processes and policies and share with
        the right owners
      </span>
      <ButtonV2 onClick={handleAddRow}>
        <Plus className="w-4 h-4 mr-1" /> Add
      </ButtonV2>
    </div>
  );

  const renderDiscardChangesButton = () => (
    <ButtonV2
      variant="outline"
      onClick={handleDiscardChanges}
      disabled={isSaving}
      className={cn(
        'font-sans font-medium text-sm leading-5',
        isSaving && 'opacity-50 cursor-not-allowed'
      )}
      style={{
        width: '137.5px',
        height: '36px',
        borderColor: '#3551F3',
        color: '#3551F3',
        gap: '8px',
      }}
    >
      Discard Changes
    </ButtonV2>
  );

  const renderSaveButton = () => {
    const isDisabled = isSaving || !hasAnyChanges || hasInvalidAddedRow;

    return (
      <ButtonV2
        variant="primary"
        onClick={handleSave}
        disabled={isDisabled}
        className={cn(
          'font-sans font-medium text-sm leading-5 flex items-center justify-center w-[137.5px] h-[36px] gap-2',
          isDisabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <Save className="w-4 h-4 relative right-[4px]" />
        <span className="font-sans font-medium text-sm leading-5 align-middle">
          {isSaving ? 'Saving...' : 'Save'}
        </span>
      </ButtonV2>
    );
  };

  const renderFooter = () => (
    <div className="w-full min-h-[44px] flex justify-end items-center gap-5 rounded-lg py-2 px-2.5 border bg-[#F1F5F9] mt-4">
      <div className="flex flex-col items-end flex-1">
        <span className="font-sans font-semibold text-sm leading-5 tracking-normal text-[#737373]">
          {policyAdded.length!==0 && policyAdded[0].suggestedPolicyChange.trim()==="" 
          ? "Add a suggested policy change to enable Save" 
          : "You have made changes, save now" }
        </span>
      </div>
      {renderDiscardChangesButton()}
      {renderSaveButton()}
    </div>
  );

  const renderTable = () => (
    <BaseDataTable
      columnConfigs={columnConfigs}
      dataRows={dataRows}
      showDefaultTableBody={tableData.length === 0}
      defaultTableBody={renderDefaultTableBody()}
    />
  );

 return (
  <div className="w-full">
    {renderTable()}
    {hasAnyChanges && renderFooter()}
    {isClosureNoteModalOpen && (
      <ClosureNoteModal
        isOpen={isClosureNoteModalOpen}
        onClose={handleCloseClosureNoteModal}
        variant={modalVariant}
        initialNote={
          selectedRowIndex !== null
            ? tableData[selectedRowIndex]?.closureNote || ""
            : ""
        }
        onSave={handleSaveClosureNote}
      />
    )}
  </div>
  );
};

export default PolicyImprovementsTable;
